import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

/** Serializable shop used in CB2 preview scenarios for the hero block. */
export type ShopHeroImagePreviewShop = {
  id: string;
  name: string;
  available: boolean;
  short_description?: string;
  phone?: string;
  contact_email?: string;
  type?: { name: string };
  venue?: {
    address?: {
      street_line1?: string;
      postal_code?: string;
      city?: string;
    };
  };
  images?: {
    cover?: { url: string };
  };
  distribution_modes?: readonly { type: string; allow_grouped_order?: boolean }[];
  schedules?: readonly {
    start: number;
    end: number;
    opened: boolean;
    active: boolean;
    allowed_distribution_modes: null;
  }[];
};

/**
 * Serializable preview VM for `ShopHeroImageBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type ShopHeroImageBlockScenarioContent = {
  shop: ShopHeroImagePreviewShop | null;
  isShopLoading: boolean;
};

export const SHOP_HERO_IMAGE_LEGACY_FIELD_ORDER = [
  "display_category",
  "display_address",
  "display_more_info_button",
  "display_image",
  "override_image",
  "title_size",
  "title_font",
  { type: "separator" },
  "background_color",
  "shop_name_color",
  "text_color",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  shopHeroImageBlockDescriptor: _generatedShopHeroImageBlockDescriptor,
  shopHeroImageBlockBlocDefinition,
  shopHeroImageBlockDashboardLayout,
  shopHeroImageBlockConfig,
  shopHeroImageBlockResolveConfig,
  shopHeroImageBlockProperties,
  shopHeroImageBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Shop - Hero image",
    componentType: "ShopHeroImageBlockComponent",
    category: "media",
    description: "Shop hero area with optional image, category, address and info button.",
    keywords: ["shop", "hero", "image"],
    allowedIn: [{ bloc: "ShopPageComponent", slot: "body" }],
    sharedFields: {},
  },

  () => ({
    display_category: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-hero-image.display-category.label",
        help: "pages.blocks.shop-hero-image.display-category.help",
      },
    }),
    display_address: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-hero-image.display-address.label",
      },
    }),
    display_more_info_button: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-hero-image.display-more-info-button.label",
      },
    }),
    display_image: propertyValue({
      type: "checkbox",
      value: true,
      options: {
        label: "pages.blocks.shop-hero-image.display-image.label",
        help: "pages.blocks.shop-hero-image.display-image.help",
      },
    }),
    override_image: propertyValue({
      type: "image",
      value: null,
      options: {
        label: "pages.blocks.shop-hero-image.override-image.label",
        help: "pages.blocks.shop-hero-image.override-image.help",
      },
    }),
    title_size: propertyValue({
      type: "select",
      value: "M",
      options: {
        label: "pages.blocks.shop-hero-image.title-size.label",
        choices: [
          {
            value: "XS",
            label: "pages.blocks.shop-hero-image.title-size.options.XS",
          },
          {
            value: "S",
            label: "pages.blocks.shop-hero-image.title-size.options.S",
          },
          {
            value: "M",
            label: "pages.blocks.shop-hero-image.title-size.options.M",
          },
          {
            value: "L",
            label: "pages.blocks.shop-hero-image.title-size.options.L",
          },
        ],
      },
    }),
    title_font: propertyValue({
      type: "select",
      value: "accent",
      options: {
        label: "pages.blocks.shop-hero-image.title-font.label",
        choices: [
          {
            value: "brand",
            label: "pages.blocks.shop-hero-image.title-font.options.brand",
          },
          {
            value: "accent",
            label: "pages.blocks.shop-hero-image.title-font.options.accent",
          },
          {
            value: "base",
            label: "pages.blocks.shop-hero-image.title-font.options.base",
          },
        ],
      },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "transparent" },
      options: {
        label: "pages.blocks.shop-hero-image.background-color.label",
      },
    }),
    shop_name_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-800" },
      options: {
        label: "pages.blocks.shop-hero-image.shop-name-color.label",
      },
    }),
    text_color: propertyValue({
      type: "color",
      value: { type: "palette", value: "neutral-600" },
      options: {
        label: "pages.blocks.shop-hero-image.text-color.label",
        help: "pages.blocks.shop-hero-image.text-color.help",
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.shop-hero-image.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.shop-hero-image.fieldset.appearance.label",
            fields: ["background_color"],
          },
          {
            legend: "pages.blocks.shop-hero-image.fieldset.image.label",
            fields: [
              "display_image",
              {
                prop: "override_image",
                visibleWhen: { display_image: true },
              },
            ],
          },
          {
            legend: "pages.blocks.shop-hero-image.fieldset.name.label",
            fields: ["title_size", "title_font", "shop_name_color"],
          },
          {
            legend: "pages.blocks.shop-hero-image.fieldset.info.label",
            fields: [
              "display_category",
              "display_address",
              "display_more_info_button",
              "text_color",
            ],
          },
        ],
      },
    ],
  }),
);

export function shopHeroImageBlockDescriptor(): IBlockDescriptor;
export function shopHeroImageBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function shopHeroImageBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedShopHeroImageBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedShopHeroImageBlockDescriptor,
    shopHeroImageBlockProperties,
    fieldOrder,
  );
}

export {
  shopHeroImageBlockBlocDefinition,
  shopHeroImageBlockDashboardLayout,
  shopHeroImageBlockConfig,
  shopHeroImageBlockResolveConfig,
  shopHeroImageBlockProperties,
  shopHeroImageBlockDefinitionSetupScenarios,
};

export type ShopHeroImageBlockConfig = typeof shopHeroImageBlockConfig;
